import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../organisms/Dialog';
import SelectTextField from '../../molecules/SelectTextField';
import { monitoringApi, Classification, UpdateRiskLevelDTO, MonitoringApiError } from '../../../services/monitoringApi';

interface MonitoringRiskLevelModalProps {
    isOpen: boolean;
    item: Classification;
    onClose: () => void;
    onSuccess: () => void;
}

type RiskLevel = 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';

const RISK_OPTIONS: { value: RiskLevel; label: string; color: string }[] = [
    { value: 'EXTREME', label: 'Extremo', color: 'bg-red-700' },
    { value: 'HIGH', label: 'Alto', color: 'bg-red-500' },
    { value: 'MEDIUM', label: 'Médio', color: 'bg-yellow-400' },
    { value: 'LOW', label: 'Baixo', color: 'bg-green-500' },
];

const MonitoringRiskLevelModal: React.FC<MonitoringRiskLevelModalProps> = ({ isOpen, item, onClose, onSuccess }) => {
    const [newRiskLevel, setNewRiskLevel] = useState<RiskLevel | ''>('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');

    const handleClose = () => {
        setNewRiskLevel('');
        setNotes('');
        setApiError('');
        onClose();
    };

    const canSubmit = () => {
        return newRiskLevel !== '' && !isSubmitting;
    };

    const handleSubmit = async () => {
        if (!canSubmit()) return;

        setIsSubmitting(true);
        setApiError('');
        try {
            const dto: UpdateRiskLevelDTO = {
                risk_level: newRiskLevel as RiskLevel,
                notes: notes.trim() || `Risco alterado de ${item.risk_level} para ${newRiskLevel}`,
            };
            await monitoringApi.updateRiskLevel(item.person_id, dto);
            toast.success('Nível de risco atualizado com sucesso');
            onSuccess();
            handleClose();
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                setApiError(e.message);
            } else {
                setApiError('Erro ao atualizar nível de risco');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentRisk = item.risk_level as RiskLevel;

    const body = (
        <div className="p-4 space-y-4">
            {/* Info atual */}
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                <p><span className="font-medium">Pessoa:</span> {item.person_name || item.person_id}</p>
                <p>
                    <span className="font-medium">Risco atual:</span>{' '}
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white ${RISK_OPTIONS.find(r => r.value === currentRisk)?.color || 'bg-gray-500'}`}>
                        {RISK_OPTIONS.find(r => r.value === currentRisk)?.label || currentRisk}
                    </span>
                </p>
            </div>

            {/* Select novo risco */}
            <SelectTextField
                id="newRiskLevel"
                name="newRiskLevel"
                label="Novo Nível de Risco *"
                value={newRiskLevel}
                onChange={(e) => { setNewRiskLevel(e.target.value as RiskLevel | ''); setApiError(''); }}
                items={RISK_OPTIONS.filter(r => r.value !== currentRisk).map(r => ({ value: r.value, label: r.label }))}
                emptyLabel="Selecione o novo nível"
            />

            {/* Observação */}
            <div>
                <label htmlFor="riskNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observação
                </label>
                <textarea
                    id="riskNotes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Motivo da alteração"
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-1">
                    {notes.length}/500
                </p>
            </div>

            {/* Aviso */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                <span className="text-blue-500 text-sm mt-0.5">&#9432;</span>
                <p className="text-xs text-blue-800 dark:text-blue-300">
                    A frequência de monitoramento será recalculada pelo sistema conforme o novo nível.
                </p>
            </div>

            {/* Erro da API */}
            {apiError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                    <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
                </div>
            )}
        </div>
    );

    const footer = (
        <div className="flex gap-3">
            <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600"
            >
                Cancelar
            </button>
            <button
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isSubmitting ? 'Salvando...' : 'Confirmar'}
            </button>
        </div>
    );

    return (
        <Dialog
            id="monitoring-risk-level-modal"
            show={isOpen}
            onClose={handleClose}
            header="Alterar Nível de Risco"
            body={body}
            footer={footer}
            size="md"
        />
    );
};

export default MonitoringRiskLevelModal;
